import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

type Placement = "top" | "bottom" | "left" | "right";

type PopoverProps = {
    visible: boolean;
    onClose: () => void;
    placement?: Placement;
    className?: string;
    zIndex?: number;
    children: React.ReactNode;
    content: React.ReactNode;
};

const MARGIN = 8;
const ARROW_SIZE = 6;

type Coords = {
    top: number;
    left: number;
    arrowTop: number;
    arrowLeft: number;
    actualPlacement: Placement;
};

const computePosition = (
    triggerRect: DOMRect,
    menuW: number,
    menuH: number,
    preferred: Placement
): Coords => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const placements: Placement[] = [preferred, ...["top", "bottom", "left", "right"].filter(p => p !== preferred) as Placement[]];

    for(const placement of placements) {
        let top = 0;
        let left = 0;

        if(placement === "top") {
            top = triggerRect.top - menuH - ARROW_SIZE - 4;
            left = triggerRect.left + triggerRect.width / 2 - menuW / 2;
        } else if(placement === "bottom") {
            top = triggerRect.bottom + ARROW_SIZE + 4;
            left = triggerRect.left + triggerRect.width / 2 - menuW / 2;
        } else if(placement === "left") {
            top = triggerRect.top + triggerRect.height / 2 - menuH / 2;
            left = triggerRect.left - menuW - ARROW_SIZE - 4;
        } else {
            top = triggerRect.top + triggerRect.height / 2 - menuH / 2;
            left = triggerRect.right + ARROW_SIZE + 4;
        }

        left = Math.max(MARGIN, Math.min(left, vw - menuW - MARGIN));
        const fitsV = top >= MARGIN && top + menuH <= vh - MARGIN;

        if(fitsV) {
            const arrow = computeArrow(placement, triggerRect, top, left, menuW, menuH);

            return { top, left, arrowTop: arrow.top, arrowLeft: arrow.left, actualPlacement: placement };
        }
    }

    let top = triggerRect.top - menuH - ARROW_SIZE - 4;
    let left = triggerRect.left + triggerRect.width / 2 - menuW / 2;
    left = Math.max(MARGIN, Math.min(left, vw - menuW - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - menuH - MARGIN));
    const arrow = computeArrow("top", triggerRect, top, left, menuW, menuH);

    return { top, left, arrowTop: arrow.top, arrowLeft: arrow.left, actualPlacement: "top" };
};

const computeArrow = (
    placement: Placement,
    triggerRect: DOMRect,
    menuTop: number,
    menuLeft: number,
    menuW: number,
    menuH: number
): { top: number; left: number } => {
    if(placement === "top" || placement === "bottom") {
        const centerX = triggerRect.left + triggerRect.width / 2;

        return {
            left: Math.max(10, Math.min(centerX - menuLeft, menuW - 10)),
            top: placement === "top" ? menuH - 1 : -ARROW_SIZE + 1,
        };
    }

    const centerY = triggerRect.top + triggerRect.height / 2;

    return {
        top: Math.max(10, Math.min(centerY - menuTop, menuH - 10)),
        left: placement === "left" ? menuW - 1 : -ARROW_SIZE + 1,
    };
};

const ARROW_COLOR = "rgba(17,24,39,0.95)";

const arrowStyle = (placement: Placement): React.CSSProperties => {
    const base: React.CSSProperties = { position: "absolute", width: 0, height: 0 };

    if(placement === "top") {
        return { ...base, borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderTop: `${ARROW_SIZE}px solid ${ARROW_COLOR}` };
    }

    if(placement === "bottom") {
        return { ...base, borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid ${ARROW_COLOR}` };
    }

    if(placement === "left") {
        return { ...base, borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`, borderLeft: `${ARROW_SIZE}px solid ${ARROW_COLOR}` };
    }

    return { ...base, borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid ${ARROW_COLOR}` };
};

const motionOffset = (placement: Placement) => {
    if(placement === "top") {
        return { y: 6 };
    }

    if(placement === "bottom") {
        return { y: -6 };
    }

    if(placement === "left") {
        return { x: 6 };
    }

    return { x: -6 };
};

const Popover = ({ visible, onClose, placement = "top", className, zIndex = 300, children, content }: PopoverProps) => {
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const [coords, setCoords] = React.useState<Coords | null>(null);

    const measure = React.useCallback(() => {
        if(!triggerRef.current || !menuRef.current) {
            return;
        }

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const menuW = menuRef.current.offsetWidth;
        const menuH = menuRef.current.offsetHeight;

        setCoords(computePosition(triggerRect, menuW, menuH, placement));
    }, [placement]);

    React.useEffect(() => {
        if(!visible) {
            setCoords(null);
            return;
        }

        let rafId: number;

        const loop = () => {
            measure();
            rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(rafId);
        };
    }, [visible, measure]);

    const offset = coords ? motionOffset(coords.actualPlacement) : { y: 6 };

    return (
        <>
            <div ref={triggerRef} className={`inline-flex ${className || ""}`}>
                {children}
            </div>

            {ReactDOM.createPortal(
                <AnimatePresence mode="wait">
                    {visible && (
                        <>
                            <div className="fixed inset-0" style={{ zIndex }} onClick={onClose} />
                            <motion.div
                                ref={menuRef}
                                className="fixed"
                                style={{
                                    zIndex,
                                    top: coords ? `${coords.top}px` : 0,
                                    left: coords ? `${coords.left}px` : 0,
                                    visibility: coords ? "visible" : "hidden",
                                }}
                                initial={{ opacity: 0, scale: 0.9, ...offset }}
                                animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2, ease: "easeIn" } }}
                                transition={{ duration: 0.25, ease: "easeOut" }}
                            >
                                {content}
                                {coords && (
                                    <div
                                        style={{
                                            ...arrowStyle(coords.actualPlacement),
                                            top: `${coords.arrowTop}px`,
                                            left: `${coords.arrowLeft - ARROW_SIZE}px`,
                                        }}
                                    />
                                )}
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </>
    );
};

export default Popover;
